// Shell slots. Proton's apps each supply their own sidebar content (Mail's
// folders, Calendar's mini-month, Drive's tree) into one shared frame. Here the
// Shell owns that frame and exposes DOM slots; a route portals its primary
// button / navigation / footer / header search into them, so the route keeps
// all of its own state while the chrome stays single-sourced.
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

export type SlotName = "primary" | "nav" | "footer" | "search" | "headerActions";

interface Slots {
  nodes: Partial<Record<SlotName, HTMLElement | null>>;
  register: (name: SlotName) => (el: HTMLElement | null) => void;
  /** Mobile off-canvas sidebar. */
  sidebarExpanded: boolean;
  setSidebarExpanded: (v: boolean) => void;
}

const Ctx = createContext<Slots | null>(null);

export function ShellSlotsProvider({ children }: { children: ReactNode }) {
  const [nodes, setNodes] = useState<Slots["nodes"]>({});
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  // One stable callback ref per slot. A fresh function each render would make
  // React detach (null) and re-attach the ref on every commit, and each of
  // those calls updates state: an infinite render loop.
  const refs = useRef<Partial<Record<SlotName, (el: HTMLElement | null) => void>>>({});
  const register = useCallback<Slots["register"]>((name) => {
    if (!refs.current[name]) {
      refs.current[name] = (el) => {
        if (!el) return; // keep the last node; slots live as long as the Shell
        setNodes((prev) => (prev[name] === el ? prev : { ...prev, [name]: el }));
      };
    }
    return refs.current[name]!;
  }, []);
  const value = useMemo(() => ({ nodes, register, sidebarExpanded, setSidebarExpanded }), [nodes, register, sidebarExpanded]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useShellSlots(): Slots {
  const v = useContext(Ctx);
  if (!v) throw new Error("useShellSlots outside ShellSlotsProvider");
  return v;
}

/** True when a route has filled the given slot (lets the Shell show defaults). */
export function useSlotFilled(name: SlotName): boolean {
  const { nodes } = useShellSlots();
  return !!nodes[name]?.childElementCount;
}

function Slot({ name, children }: { name: SlotName; children: ReactNode }) {
  const ctx = useContext(Ctx);
  const node = ctx?.nodes[name];
  return node ? createPortal(children, node) : null;
}

export const SidebarPrimary = ({ children }: { children: ReactNode }) => <Slot name="primary">{children}</Slot>;
export const SidebarNav = ({ children }: { children: ReactNode }) => <Slot name="nav">{children}</Slot>;
export const SidebarFooter = ({ children }: { children: ReactNode }) => <Slot name="footer">{children}</Slot>;
export const HeaderSearch = ({ children }: { children: ReactNode }) => <Slot name="search">{children}</Slot>;
export const HeaderActions = ({ children }: { children: ReactNode }) => <Slot name="headerActions">{children}</Slot>;
